import { ReactNode, useCallback, useMemo, useState } from 'react';

import type { ActionBarAction } from './actionBarAction';
import {
	ActionBarContext,
	type ActionBarContextValue,
	type RegisterActionsOptions,
} from './actionBarContext';

interface RegisteredSource {
	actions: ActionBarAction[];
	priority: number;
	order: number;
	overflowLimit?: number;
}

const DEFAULT_OVERFLOW_LIMIT = 2;

export const ActionBarProvider = ({ children }: { children: ReactNode }) => {
	const [sources, setSources] = useState<Map<string, RegisteredSource>>(() => new Map());

	const registerActions = useCallback(
		(sourceId: string, actions: ActionBarAction[], options?: RegisterActionsOptions) => {
			const { priority = 0, overflowLimit } = options ?? {};

			setSources((prev) => {
				const existing = prev.get(sourceId);
				const next = new Map(prev);

				next.set(sourceId, {
					actions,
					priority,
					order: existing?.order ?? prev.size,
					overflowLimit,
				});

				return next;
			});
		},
		[]
	);

	const unregisterActions = useCallback((sourceId: string) => {
		setSources((prev) => {
			if (!prev.has(sourceId)) {
				return prev;
			}

			const next = new Map(prev);
			next.delete(sourceId);
			return next;
		});
	}, []);

	const { actions, overflowLimit } = useMemo(() => {
		const orderedSources = Array.from(sources.values()).sort((a, b) => {
			if (a.priority === b.priority) {
				return a.order - b.order;
			}
			return a.priority - b.priority;
		});

		return {
			actions: orderedSources.flatMap((entry) => entry.actions),
			overflowLimit:
				orderedSources.find((entry) => entry.overflowLimit !== undefined)?.overflowLimit ??
				DEFAULT_OVERFLOW_LIMIT,
		};
	}, [sources]);

	const value = useMemo<ActionBarContextValue>(
		() => ({
			actions,
			overflowLimit,
			registerActions,
			unregisterActions,
		}),
		[actions, overflowLimit, registerActions, unregisterActions]
	);

	return <ActionBarContext value={value}>{children}</ActionBarContext>;
};

export default ActionBarProvider;
